import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Mail, Send, Eye, Users, AlertTriangle, History, CheckCircle2, MousePointerClick,
  FileText, Filter, BarChart3, Trash2, Pencil, Save, Plus, ArrowDownToLine, Repeat, Power,
} from "lucide-react";
import { PageHeader, SectionCard, EmptyState, StatCard } from "../components/ui/kaizen";

const AUDIENCES = [
  { id: "active", label: "Membres actifs" },
  { id: "active_plus_private", label: "Actifs + clients privés" },
  { id: "inactive", label: "Anciens membres" },
  { id: "essai", label: "En essai" },
  { id: "all_with_email", label: "Tous les contacts" },
  { id: "select", label: "Choisir les membres" },
  { id: "custom", label: "Liste précise" },
];

const TABS = [
  { id: "compose", label: "Composer", icon: Send },
  { id: "templates", label: "Modèles", icon: FileText },
  { id: "segments", label: "Segments", icon: Filter },
  { id: "recurring", label: "Campagnes auto", icon: Repeat },
  { id: "dashboard", label: "Tableau de bord", icon: BarChart3 },
];

export default function MemberEmailBroadcastModule() {
  const [tab, setTab] = useState("compose");

  // Compose state
  const [audience, setAudience] = useState("active");
  const [discipline, setDiscipline] = useState("all");
  const [customEmails, setCustomEmails] = useState("");
  // Sélection manuelle de membres (audience "select")
  const [members, setMembers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectBase, setSelectBase] = useState("active");
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [fuEnabled, setFuEnabled] = useState(false);
  const [fuDelay, setFuDelay] = useState(3);
  const [fuSubject, setFuSubject] = useState("");
  const [fuMessage, setFuMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // CRM state
  const [disciplines, setDisciplines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [dash, setDash] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [rcForm, setRcForm] = useState({
    name: "", audience: "inactive", discipline: "all", subject: "", message: "",
    image_url: "", day_of_month: 1, followup_enabled: true, followup_delay_days: 3, followup_message: "",
  });

  const parseCustom = () =>
    customEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

  const loadPreview = async (aud = audience, disc = discipline) => {
    try {
      const { data } = await api.post("/member-email/preview", {
        audience: aud, discipline: disc === "all" ? null : disc,
        custom_emails: aud === "custom" ? parseCustom() : [],
      });
      setPreview(data);
    } catch (e) { toast.error("Erreur d'aperçu de l'audience."); }
  };

  const loadDisciplines = async () => {
    try { const { data } = await api.get("/member-email/disciplines"); setDisciplines(data.disciplines || []); } catch (e) {}
  };
  const loadTemplates = async () => {
    try { const { data } = await api.get("/member-email/templates"); setTemplates(data.templates || []); } catch (e) {}
  };
  const loadSegments = async () => {
    try { const { data } = await api.get("/member-email/segments"); setSegments(data.segments || []); } catch (e) {}
  };
  const loadDashboard = async () => {
    try { const { data } = await api.get("/member-email/dashboard"); setDash(data); } catch (e) {}
  };
  const loadRecurring = async () => {
    try { const { data } = await api.get("/member-email/recurring"); setRecurring(data.campaigns || []); } catch (e) {}
  };

  const loadMembers = async (base = selectBase, disc = discipline) => {
    setLoadingMembers(true);
    try {
      const { data } = await api.get(`/member-email/members?audience=${base}&discipline=${disc}`);
      setMembers(data.members || []);
    } catch (e) { toast.error("Erreur de chargement des membres."); }
    setLoadingMembers(false);
  };
  const toggleMember = (email) =>
    setSelectedEmails((prev) => prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]);
  const filteredMembers = members.filter((m) => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    return (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
  });

  useEffect(() => { loadPreview("active", "all"); loadDisciplines(); loadTemplates(); loadSegments(); loadDashboard(); loadRecurring(); }, []);
  useEffect(() => {
    const raw = localStorage.getItem("prefill_email");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setSubject(p.subject || ""); setMessage(p.message || ""); setTab("compose");
        toast.success("Courriel pré-rempli depuis la campagne « Les 3 ».");
      } catch (e) {}
      localStorage.removeItem("prefill_email");
    }
  }, []);
  useEffect(() => { if (audience !== "custom" && audience !== "select") loadPreview(audience, discipline); }, [audience, discipline]); // eslint-disable-line
  // Charge la liste des membres quand on passe en mode « Choisir les membres »
  useEffect(() => { if (audience === "select") loadMembers(selectBase, discipline); }, [audience, selectBase, discipline]); // eslint-disable-line

  const isSelect = audience === "select";
  const recipientCount = isSelect ? selectedEmails.length : (preview?.total || 0);

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Objet et message requis."); return; }
    const count = recipientCount;
    if (!count) { toast.error("Aucun destinataire."); return; }
    if (!window.confirm(`Envoyer ce courriel à ${count} destinataire(s) ? Cette action est irréversible.`)) return;
    setBusy(true);
    try {
      const sendAudience = isSelect ? "custom" : audience;
      const sendCustom = isSelect ? selectedEmails : (audience === "custom" ? parseCustom() : []);
      const { data } = await api.post("/member-email/send?confirm=true", {
        audience: sendAudience, discipline: (isSelect || discipline === "all") ? null : discipline,
        custom_emails: sendCustom,
        subject: subject.trim(), message, image_url: imageUrl.trim() || null,
        followup_enabled: fuEnabled, followup_delay_days: Number(fuDelay) || 3,
        followup_subject: fuSubject.trim() || null, followup_message: fuMessage.trim() || null,
      });
      if (data.ok && data.started) {
        toast.success(`Envoi lancé vers ${data.total} destinataire(s) 🚀`);
        toast.info("L'envoi se termine en arrière-plan (~1-2 min). Le tableau de bord se met à jour automatiquement. N'envoie pas une 2ᵉ fois.");
        if (fuEnabled) toast.info(`Relance auto programmée dans ${fuDelay} jour(s) pour les non-ouvreurs.`);
        setSubject(""); setMessage(""); setImageUrl(""); setFuEnabled(false); setFuSubject(""); setFuMessage(""); setSelectedEmails([]);
        setTimeout(loadDashboard, 4000);
      } else if (data.ok) {
        toast.success(`Envoyé : ${data.sent} · Échecs : ${data.failed}`);
        if (data.failed) toast.warning(`${data.failed} échec(s). Voir le tableau de bord.`);
        if (fuEnabled) toast.info(`Relance auto programmée dans ${fuDelay} jour(s) pour les non-ouvreurs.`);
        setSubject(""); setMessage(""); setImageUrl(""); setFuEnabled(false); setFuSubject(""); setFuMessage(""); setSelectedEmails([]); loadDashboard();
      } else { toast.error("Envoi non confirmé."); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi.");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Objet et message requis."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/member-email/test", {
        subject: subject.trim(), message, image_url: imageUrl.trim() || null,
      });
      toast.success(`Courriel test envoyé à ${data.sent_to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi test.");
    } finally { setBusy(false); }
  };

  // ── Modèles ──
  const applyTemplate = (t) => {
    setSubject(t.subject || ""); setMessage(t.body || ""); setImageUrl(t.image_url || "");
    setTab("compose");
    toast.success(`Modèle « ${t.name} » chargé dans le composeur.`);
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du modèle :", subject.trim().slice(0, 40));
    if (!name || !name.trim()) return;
    try {
      await api.post("/member-email/templates", {
        name: name.trim(), subject: subject.trim(), body: message, image_url: imageUrl.trim() || null,
      });
      toast.success("Modèle enregistré.");
      loadTemplates();
    } catch (e) { toast.error("Échec de l'enregistrement du modèle."); }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await api.delete(`/member-email/templates/${id}`); loadTemplates(); toast.success("Modèle supprimé."); }
    catch (e) { toast.error("Échec de la suppression."); }
  };

  // ── Segments ──
  const [segName, setSegName] = useState("");
  const [segAud, setSegAud] = useState("active");
  const [segDisc, setSegDisc] = useState("all");
  const createSegment = async () => {
    if (!segName.trim()) { toast.error("Nom du segment requis."); return; }
    try {
      await api.post("/member-email/segments", {
        name: segName.trim(), audience: segAud, discipline: segDisc === "all" ? null : segDisc,
      });
      toast.success("Segment enregistré.");
      setSegName(""); loadSegments();
    } catch (e) { toast.error("Échec de l'enregistrement du segment."); }
  };
  const deleteSegment = async (id) => {
    if (!window.confirm("Supprimer ce segment ?")) return;
    try { await api.delete(`/member-email/segments/${id}`); loadSegments(); toast.success("Segment supprimé."); }
    catch (e) { toast.error("Échec de la suppression."); }
  };
  const applySegment = (s) => {
    setAudience(s.audience); setDiscipline(s.discipline || "all"); setTab("compose");
    toast.success(`Segment « ${s.name} » appliqué (${s.count} destinataires).`);
  };

  const inputCls = "w-full mt-1.5 px-3 py-2 border border-zinc-700 rounded-lg text-sm";

  // ── Campagnes auto ──
  const createRecurring = async () => {
    if (!rcForm.name.trim() || !rcForm.subject.trim() || !rcForm.message.trim()) {
      toast.error("Nom, objet et message requis."); return;
    }
    try {
      await api.post("/member-email/recurring", {
        ...rcForm, discipline: rcForm.discipline === "all" ? null : rcForm.discipline,
        day_of_month: Number(rcForm.day_of_month) || 1, followup_delay_days: Number(rcForm.followup_delay_days) || 3,
      });
      toast.success("Campagne automatique créée.");
      setRcForm({ name: "", audience: "inactive", discipline: "all", subject: "", message: "", image_url: "", day_of_month: 1, followup_enabled: true, followup_delay_days: 3, followup_message: "" });
      loadRecurring();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la création."); }
  };
  const toggleRecurring = async (c) => {
    try {
      await api.put(`/member-email/recurring/${c.id}`, { ...c, enabled: !c.enabled });
      loadRecurring();
      toast.success(!c.enabled ? "Campagne activée." : "Campagne mise en pause.");
    } catch (e) { toast.error("Échec de la mise à jour."); }
  };
  const deleteRecurring = async (id) => {
    if (!window.confirm("Supprimer cette campagne automatique ?")) return;
    try { await api.delete(`/member-email/recurring/${id}`); loadRecurring(); toast.success("Campagne supprimée."); }
    catch (e) { toast.error("Échec de la suppression."); }
  };
  const dryRunRecurring = async () => {
    try {
      const { data } = await api.post("/member-email/recurring/run?dry_run=true");
      const due = (data.processed || []).length;
      toast.success(due ? `${due} campagne(s) due(s) aujourd'hui (simulation).` : "Aucune campagne due aujourd'hui.");
    } catch (e) { toast.error("Échec de la simulation."); }
  };


  return (
    <div className="space-y-6" data-testid="member-email-module">
      <PageHeader title="📣 Diffusion membres (mini-CRM)" subtitle="Envoi fiable via Resend · segments, modèles & suivi des taux d'ouverture." icon={Mail} />

      {preview && !preview.resend_configured && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="resend-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Resend n'est pas encore configuré (clé API manquante). L'aperçu fonctionne, mais l'envoi est désactivé.</span>
        </div>
      )}
      {preview && preview.resend_configured && preview.test_mode && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="testmode-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span><strong>Mode test Resend</strong> : tant que le domaine n'est pas vérifié, seuls les courriels vers l'adresse propriétaire seront livrés.</span>
        </div>
      )}

      {/* Onglets */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-[#D90429] text-white" : "bg-transparent text-zinc-400 hover:bg-zinc-800"
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── COMPOSER ── */}
      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <SectionCard title="Composer le courriel" icon={Send}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Audience</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {AUDIENCES.map((a) => (
                      <button key={a.id} data-testid={`audience-${a.id}`} onClick={() => setAudience(a.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                          audience === a.id ? "bg-[#D90429] text-white border-[#D90429]" : "bg-zinc-800 text-zinc-300 border-zinc-800 hover:bg-zinc-800/60"
                        }`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>

                {audience !== "custom" && (
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Discipline</label>
                    <select data-testid="discipline-select" value={discipline} onChange={(e) => setDiscipline(e.target.value)} className={inputCls}>
                      <option value="all">Toutes les disciplines</option>
                      {disciplines.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.count})</option>)}
                    </select>
                  </div>
                )}

                {audience === "custom" && (
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Adresses (virgule / espace / ligne)</label>
                    <textarea data-testid="custom-emails-input" value={customEmails} onChange={(e) => setCustomEmails(e.target.value)}
                      onBlur={() => loadPreview("custom")} rows={3} className={inputCls} placeholder="nom@exemple.com, autre@exemple.com" />
                  </div>
                )}

                {audience === "select" && (
                  <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-800/60" data-testid="member-picker">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <label className="text-sm font-semibold text-zinc-300">Base :</label>
                      <select data-testid="select-base" value={selectBase} onChange={(e) => setSelectBase(e.target.value)}
                        className="px-2 py-1.5 border border-zinc-700 rounded-lg text-sm">
                        {AUDIENCES.filter((a) => a.id !== "custom" && a.id !== "select").map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                      </select>
                      <input data-testid="member-search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Rechercher un nom ou courriel…" className="flex-1 min-w-[160px] px-3 py-1.5 border border-zinc-700 rounded-lg text-sm" />
                    </div>
                    <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                      <span><strong>{selectedEmails.length}</strong> sélectionné(s) sur {members.length}</span>
                      <div className="flex gap-2">
                        <button data-testid="select-all-members" onClick={() => setSelectedEmails(filteredMembers.map((m) => m.email))}
                          className="font-semibold text-[#D90429] hover:underline">Tout cocher ({filteredMembers.length})</button>
                        <button data-testid="clear-members" onClick={() => setSelectedEmails([])}
                          className="font-semibold text-zinc-400 hover:underline">Tout décocher</button>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg bg-zinc-800 divide-y divide-zinc-800" data-testid="member-list">
                      {loadingMembers ? (
                        <p className="text-sm text-zinc-500 p-3">Chargement des membres…</p>
                      ) : filteredMembers.length === 0 ? (
                        <p className="text-sm text-zinc-500 p-3">Aucun membre trouvé.</p>
                      ) : filteredMembers.map((m) => (
                        <label key={m.email} data-testid={`member-row-${m.email}`}
                          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-zinc-800/60">
                          <input type="checkbox" className="w-4 h-4 accent-[#D90429]"
                            checked={selectedEmails.includes(m.email)} onChange={() => toggleMember(m.email)} />
                          <span className="font-medium text-zinc-200">{m.name || "(sans nom)"}</span>
                          <span className="text-zinc-500 truncate">· {m.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold text-zinc-300">Objet</label>
                  <input data-testid="subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="Ex. : Bonne Saint-Jean !" />
                </div>

                <div>
                  <label className="text-sm font-semibold text-zinc-300">Message</label>
                  <textarea data-testid="message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={9} className={inputCls} placeholder="Écris ton message ici. Les sauts de ligne sont conservés." />
                </div>

                <div>
                  <label className="text-sm font-semibold text-zinc-300">Image (optionnel)</label>
                  <input data-testid="image-url-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={inputCls} placeholder="Colle l'URL d'une image (bannière, affiche…)" />
                  {imageUrl.trim() && <img src={imageUrl.trim()} alt="aperçu" className="mt-2 max-h-40 rounded-lg border border-zinc-800" />}
                </div>

                <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-800/60" data-testid="followup-section">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="followup-toggle" checked={fuEnabled} onChange={(e) => setFuEnabled(e.target.checked)} className="w-4 h-4 accent-[#D90429]" />
                    <span className="text-sm font-semibold text-zinc-200">🔁 Relance automatique des non-ouvreurs</span>
                  </label>
                  {fuEnabled && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-zinc-400">Envoyer la relance après</span>
                        <input type="number" min="1" max="30" data-testid="followup-delay" value={fuDelay} onChange={(e) => setFuDelay(e.target.value)} className="w-16 px-2 py-1 border border-zinc-700 rounded text-center" />
                        <span className="text-zinc-400">jour(s) — aux non-ouvreurs.</span>
                      </div>
                      <input data-testid="followup-subject" value={fuSubject} onChange={(e) => setFuSubject(e.target.value)} className="w-full px-3 py-2 border border-zinc-700 rounded-lg text-sm" placeholder="Objet de la relance (optionnel)" />
                      <textarea data-testid="followup-message" value={fuMessage} onChange={(e) => setFuMessage(e.target.value)} rows={4} className="w-full px-3 py-2 border border-zinc-700 rounded-lg text-sm" placeholder="Message de la relance (requis si activé)" />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button data-testid="send-broadcast-btn" onClick={send} disabled={busy || !preview?.resend_configured}
                    className="flex items-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50">
                    <Send size={16} /> {busy ? "Envoi en cours…" : `Envoyer à ${recipientCount} destinataire(s)`}
                  </button>
                  <button data-testid="send-test-btn" onClick={sendTest} disabled={busy || !preview?.resend_configured}
                    className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 hover:bg-zinc-800/60 text-zinc-200 font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                    <Eye size={16} /> Test à moi-même
                  </button>
                  <button data-testid="save-as-template-btn" onClick={saveAsTemplate} disabled={!subject.trim() && !message.trim()}
                    className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 hover:bg-zinc-800/60 text-zinc-200 font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                    <Save size={16} /> Sauver comme modèle
                  </button>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="space-y-6">
            <SectionCard title="Audience ciblée" icon={Users}>
              <div className="text-center py-2">
                <p className="text-4xl font-black text-zinc-100" data-testid="audience-count">{isSelect ? selectedEmails.length : (preview?.total ?? "—")}</p>
                <p className="text-xs text-zinc-400">destinataires{isSelect ? " sélectionnés" : ""}</p>
              </div>
              <p className="text-xs text-zinc-400 mt-2">Expéditeur : <strong>{preview?.sender || "—"}</strong></p>
              {preview?.excluded && (preview.excluded.optout + preview.excluded.test + preview.excluded.sans_courriel) > 0 && (
                <p className="text-[11px] text-amber-300/90 mt-1" data-testid="audience-excluded">Exclus : {preview.excluded.optout} désabonné(s) · {preview.excluded.test} test/démo · {preview.excluded.sans_courriel} sans courriel. Mineurs → parent/responsable. 1 adresse = 1 courriel.</p>
              )}
              {preview?.sample?.length > 0 && (
                <div className="mt-3 text-xs text-zinc-400 space-y-0.5">
                  {preview.sample.map((e) => <p key={e} className="truncate">• {e}</p>)}
                  {preview.total > preview.sample.length && <p className="text-zinc-500">+ {preview.total - preview.sample.length} autres…</p>}
                </div>
              )}
            </SectionCard>

            {segments.length > 0 && (
              <SectionCard title="Segments rapides" icon={Filter}>
                <div className="space-y-2" data-testid="quick-segments">
                  {segments.map((s) => (
                    <button key={s.id} data-testid={`quick-segment-${s.id}`} onClick={() => applySegment(s)}
                      className="w-full text-left px-3 py-2 rounded-lg border border-zinc-800 hover:border-[#D90429] hover:bg-zinc-800/60 transition-colors">
                      <p className="text-sm font-semibold text-zinc-200">{s.name}</p>
                      <p className="text-xs text-zinc-400">{s.audience_label} · {s.discipline_label} · <strong>{s.count}</strong> contacts</p>
                    </button>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        </div>
      )}

      {/* ── MODÈLES ── */}
      {tab === "templates" && (
        <SectionCard title="Modèles de courriels réutilisables" icon={FileText}>
          {templates.length === 0 ? (
            <EmptyState icon={FileText} title="Aucun modèle" description="Compose un courriel puis « Sauver comme modèle »." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-list">
              {templates.map((t) => (
                <div key={t.id} data-testid={`template-${t.id}`} className="border border-zinc-800 rounded-lg p-4 flex flex-col">
                  <p className="font-semibold text-zinc-100">{t.name}</p>
                  <p className="text-sm text-zinc-400 mt-0.5 truncate">{t.subject || "(sans objet)"}</p>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.body}</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`use-template-${t.id}`} onClick={() => applyTemplate(t)}
                      className="flex items-center gap-1 text-sm font-semibold bg-[#D90429] text-white px-3 py-1.5 rounded-lg hover:bg-[#b8031f]">
                      <ArrowDownToLine size={14} /> Utiliser
                    </button>
                    <button data-testid={`delete-template-${t.id}`} onClick={() => deleteTemplate(t.id)}
                      className="flex items-center gap-1 text-sm font-semibold bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg hover:bg-zinc-800/60">
                      <Trash2 size={14} /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-4">Astuce : compose un courriel dans l'onglet « Composer » puis clique « Sauver comme modèle ».</p>
        </SectionCard>
      )}

      {/* ── SEGMENTS ── */}
      {tab === "segments" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <SectionCard title="Nouveau segment" icon={Plus}>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Nom du segment</label>
                  <input data-testid="segment-name-input" value={segName} onChange={(e) => setSegName(e.target.value)} className={inputCls} placeholder="Ex. : Anciens Muay Thaï" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Statut</label>
                  <select data-testid="segment-audience-select" value={segAud} onChange={(e) => setSegAud(e.target.value)} className={inputCls}>
                    {AUDIENCES.filter((a) => a.id !== "custom").map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Discipline</label>
                  <select data-testid="segment-discipline-select" value={segDisc} onChange={(e) => setSegDisc(e.target.value)} className={inputCls}>
                    <option value="all">Toutes les disciplines</option>
                    {disciplines.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.count})</option>)}
                  </select>
                </div>
                <button data-testid="create-segment-btn" onClick={createSegment}
                  className="w-full flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-4 py-2.5 rounded-lg">
                  <Plus size={16} /> Enregistrer le segment
                </button>
              </div>
            </SectionCard>
          </div>
          <div className="lg:col-span-2">
            <SectionCard title="Segments sauvegardés" icon={Filter}>
              {segments.length === 0 ? (
                <EmptyState icon={Filter} title="Aucun segment" description="Crée ton premier segment d'audience à gauche." />
              ) : (
                <div className="space-y-2" data-testid="segments-list">
                  {segments.map((s) => (
                    <div key={s.id} data-testid={`segment-${s.id}`} className="flex items-center justify-between border border-zinc-800 rounded-lg p-3">
                      <div>
                        <p className="font-semibold text-zinc-100">{s.name}</p>
                        <p className="text-xs text-zinc-400">{s.audience_label} · {s.discipline_label} · <strong>{s.count}</strong> contacts</p>
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`use-segment-${s.id}`} onClick={() => applySegment(s)}
                          className="text-sm font-semibold bg-[#D90429] text-white px-3 py-1.5 rounded-lg hover:bg-[#b8031f]">Utiliser</button>
                        <button data-testid={`delete-segment-${s.id}`} onClick={() => deleteSegment(s.id)}
                          className="text-sm font-semibold bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg hover:bg-zinc-800/60"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      {/* ── CAMPAGNES AUTO ── */}
      {tab === "recurring" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="recurring-tab">
          <div className="lg:col-span-1">
            <SectionCard title="Nouvelle campagne mensuelle" icon={Plus}>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Nom</label>
                  <input data-testid="rc-name-input" value={rcForm.name} onChange={(e) => setRcForm({ ...rcForm, name: e.target.value })} className={inputCls} placeholder="Ex. : Réactivation mensuelle" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Audience</label>
                    <select data-testid="rc-audience-select" value={rcForm.audience} onChange={(e) => setRcForm({ ...rcForm, audience: e.target.value })} className={inputCls}>
                      {AUDIENCES.filter((a) => a.id !== "custom").map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-zinc-300">Jour du mois</label>
                    <input type="number" min="1" max="28" data-testid="rc-day-input" value={rcForm.day_of_month} onChange={(e) => setRcForm({ ...rcForm, day_of_month: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Discipline</label>
                  <select data-testid="rc-discipline-select" value={rcForm.discipline} onChange={(e) => setRcForm({ ...rcForm, discipline: e.target.value })} className={inputCls}>
                    <option value="all">Toutes les disciplines</option>
                    {disciplines.map((d) => <option key={d.id} value={d.id}>{d.label} ({d.count})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Objet</label>
                  <input data-testid="rc-subject-input" value={rcForm.subject} onChange={(e) => setRcForm({ ...rcForm, subject: e.target.value })} className={inputCls} placeholder="Tu nous manques 🥋" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Message</label>
                  <textarea data-testid="rc-message-input" value={rcForm.message} onChange={(e) => setRcForm({ ...rcForm, message: e.target.value })} rows={4} className={inputCls} placeholder="Reviens t'entraîner — 1er cours offert." />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="rc-followup-toggle" checked={rcForm.followup_enabled} onChange={(e) => setRcForm({ ...rcForm, followup_enabled: e.target.checked })} className="w-4 h-4 accent-[#D90429]" />
                  <span className="text-sm font-semibold text-zinc-200">🔁 Relance auto des non-ouvreurs</span>
                </label>
                {rcForm.followup_enabled && (
                  <textarea data-testid="rc-followup-message" value={rcForm.followup_message} onChange={(e) => setRcForm({ ...rcForm, followup_message: e.target.value })} rows={3} className={inputCls} placeholder="Message de relance (sinon le message principal est réutilisé)" />
                )}
                <button data-testid="create-recurring-btn" onClick={createRecurring}
                  className="w-full flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-4 py-2.5 rounded-lg">
                  <Plus size={16} /> Créer la campagne
                </button>
                <p className="text-xs text-zinc-500">⚠️ Envoi RÉEL via Resend le jour choisi, chaque mois. Mets en pause à tout moment.</p>
              </div>
            </SectionCard>
          </div>
          <div className="lg:col-span-2">
            <SectionCard title="Campagnes automatiques" icon={Repeat}>
              <button data-testid="recurring-dryrun-btn" onClick={dryRunRecurring}
                className="mb-3 text-sm font-semibold bg-zinc-800 border border-zinc-700 hover:bg-zinc-800/60 text-zinc-200 px-3 py-1.5 rounded-lg">
                Tester (simulation des campagnes dues aujourd'hui)
              </button>
              {recurring.length === 0 ? (
                <EmptyState icon={Repeat} title="Aucune campagne auto" description="Crée ta première campagne de réactivation mensuelle à gauche." />
              ) : (
                <div className="space-y-2" data-testid="recurring-list">
                  {recurring.map((c) => (
                    <div key={c.id} data-testid={`recurring-${c.id}`} className="flex items-center justify-between border border-zinc-800 rounded-lg p-3">
                      <div>
                        <p className="font-semibold text-zinc-100 flex items-center gap-2">
                          {c.name}
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${c.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-700 text-zinc-400"}`}>
                            {c.enabled ? "Active" : "En pause"}
                          </span>
                        </p>
                        <p className="text-xs text-zinc-400">Le {c.day_of_month} de chaque mois · {c.audience_label} · {c.discipline_label} · <strong>{c.count}</strong> contacts{c.followup_enabled ? " · 🔁 relance auto" : ""}</p>
                        {c.last_run_month && <p className="text-[11px] text-zinc-500">Dernier envoi : {c.last_run_month}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button data-testid={`toggle-recurring-${c.id}`} onClick={() => toggleRecurring(c)}
                          className={`text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 ${c.enabled ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-emerald-500/15 border border-emerald-200 text-emerald-700"}`}>
                          <Power size={14} /> {c.enabled ? "Pause" : "Activer"}
                        </button>
                        <button data-testid={`delete-recurring-${c.id}`} onClick={() => deleteRecurring(c.id)}
                          className="text-sm font-semibold bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-1.5 rounded-lg hover:bg-zinc-800/60"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      {/* ── DASHBOARD ── */}
      {tab === "dashboard" && (
        <div className="space-y-6" data-testid="email-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Campagnes" value={dash?.totals?.campaigns ?? 0} icon={History} />
            <StatCard label="Courriels envoyés" value={dash?.totals?.sent ?? 0} icon={Send} />
            <StatCard label="Taux d'ouverture" value={`${dash?.totals?.open_rate ?? 0}%`} icon={Eye} />
            <StatCard label="Taux de clic" value={`${dash?.totals?.click_rate ?? 0}%`} icon={MousePointerClick} />
          </div>

          <SectionCard title="Historique des campagnes" icon={History}>
            {!dash || dash.campaigns.length === 0 ? (
              <EmptyState icon={History} title="Aucune campagne" description="Tes diffusions apparaîtront ici avec leurs taux d'ouverture." />
            ) : (
              <div className="overflow-x-auto" data-testid="campaigns-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-400 border-b border-zinc-800">
                      <th className="py-2 pr-4 font-semibold">Objet</th>
                      <th className="py-2 px-2 font-semibold">Audience</th>
                      <th className="py-2 px-2 font-semibold text-right">Envoyés</th>
                      <th className="py-2 px-2 font-semibold text-right">Ouverture</th>
                      <th className="py-2 px-2 font-semibold text-right">Clic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dash.campaigns.map((c) => (
                      <tr key={c.id} data-testid={`campaign-row-${c.id}`} className="border-b border-zinc-800">
                        <td className="py-2 pr-4">
                          <p className="font-semibold text-zinc-200 truncate max-w-[260px]">{c.subject}</p>
                          {c.followup?.enabled && <span className="text-[11px] text-violet-700">🔁 {c.followup.sent ? "Relance envoyée" : "Relance prévue"}</span>}
                        </td>
                        <td className="py-2 px-2 text-zinc-400">
                          {c.audience_label}{c.discipline_label ? ` · ${c.discipline_label}` : ""}
                        </td>
                        <td className="py-2 px-2 text-right font-semibold text-zinc-200">
                          {c.sent}{c.failed > 0 && <span className="text-[#D90429]"> ({c.failed} éch.)</span>}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <span className="font-semibold text-emerald-600">{c.open_rate}%</span>
                          <span className="text-zinc-500 text-xs"> ({c.opened})</span>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <span className="font-semibold text-blue-600">{c.click_rate}%</span>
                          <span className="text-zinc-500 text-xs"> ({c.clicked})</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  );
}
